import { Centrifuge } from 'centrifuge';
import { jwtDecode } from 'jwt-decode';
import { useToast, POSITION } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import { NOTIFICATION_TYPES } from '~/constants/notificationTypes';
import type { NewNotification, NotificationsChatItem } from '~/types/Profile';

export function useWebsockets() {
  const { apiUrl } = useRuntimeConfig().public;

  const cartStore = useCartStore();
  const profileStore = useProfileStore();
  const toast = useToast();

  const centrifuge = ref<Centrifuge | null>(null);

  async function initWebSockets() {
    try {
      if (centrifuge.value) {
        centrifuge.value.disconnect();
        centrifuge.value = null;
      }

      let tokens = await fetchJwtToken();
      saveTokens(tokens.access_token, tokens.refresh_token);

      const { websocketsUri } = useRuntimeConfig().public;

      centrifuge.value = new Centrifuge(
        `wss://${import.meta.env.PROD ? websocketsUri : 'ws.okdev.ru'}/connection/websocket`,
        { getToken: getConnectionToken },
      );

      // Обработка событий соединения и само соединение
      centrifuge.value
        // .on('connecting', (ctx) => console.log(`connecting: ${ctx.code}, ${ctx.reason}`, dayjs().format()))
        // .on('connected', (ctx) => console.log(`connected over ${ctx.transport}`, dayjs().format()))
        // .on('disconnected', (ctx) => console.log(`disconnected: ${ctx.code}, ${ctx.reason}`, dayjs().format()))
        .connect();

      // Подписка на публичный канал
      const publicSub = centrifuge.value.newSubscription('public:messages');
      publicSub
        // .on('publication', (ctx) => console.log(ctx.data))
        .subscribe();

      // Декодируем токен для получения userId
      const decodedToken = jwtDecode(tokens.access_token);
      const userId = decodedToken.sub;
      // console.log(userId);

      // Подписка на канал с корзиной
      const privateCartSub = centrifuge.value.newSubscription(`cart:${userId}`, {
        getToken: getChannelSubscriptionToken,
      });
      privateCartSub
        .on('publication', (ctx) => {
          if (!ctx.data?.message) return;

          try {
            cartStore.applyCartPublication(JSON.parse(ctx.data.message));
          } catch (error) {
            if (process.env.NODE_ENV !== 'production') console.error('Invalid cart publication:', error);
          }
        })
        .subscribe();

      // Подписка на канал с уведомлениями
      const privateNotificationsSub = centrifuge.value.newSubscription(`notifications:${userId}`, {
        getToken: getChannelSubscriptionToken,
      });
      privateNotificationsSub
        .on('publication', (ctx) => {
          if (ctx.data?.message) {
            const newNotification: NewNotification = JSON.parse(ctx.data.message);

            profileStore.totalUnreadNotifications = newNotification.unread_count;
            toast.warning(
              {
                component: ToastComponent,
                props: { title: 'Новое уведомление', text: newNotification.description },
              },
              { timeout: 5000, position: POSITION.TOP_CENTER },
            );

            const notificationType = profileStore.userDetails?.notifications.find(
              (notificationType: NotificationsChatItem) => notificationType.type === newNotification.type,
            );

            if (notificationType) {
              notificationType.unread_count += 1;
              notificationType.created_at = newNotification.created_at;
              notificationType.description = newNotification.description;
            } else {
              profileStore.userDetails?.notifications.unshift({
                created_at: newNotification.created_at,
                description: newNotification.description,
                title: Object.values(NOTIFICATION_TYPES).find((v) => v.type === newNotification.type)?.title || '',
                type: newNotification.type,
                unread_count: 1,
              });
            }

            profileStore.userDetails?.notifications.sort((a: NotificationsChatItem, b: NotificationsChatItem) => {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            });
          }
        })
        .subscribe();
    } catch (error) {
      console.error('Error setting up Centrifuge:', error);
    }
  }

  async function getConnectionToken() {
    const token = localStorage.getItem('access_token');

    if (token) {
      const decodedToken = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken?.exp && decodedToken.exp < currentTime) {
        // console.log('Token expired, refreshing...', dayjs().format());
        try {
          const newTokens = await refreshJwtToken();
          saveTokens(newTokens.access_token, newTokens.refresh_token);

          return newTokens.access_token;
        } catch (error) {
          console.error('Error refreshing JWT token:', error);
          return null;
        }
      } else {
        return token;
      }
    } else {
      return await fetchJwtToken();
    }
  }

  async function getChannelSubscriptionToken(ctx: any) {
    try {
      const sub = getSubFromToken();

      const response = await fetch(`${apiUrl}1/jwt/subscription-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getConnectionToken()}`,
        },
        body: JSON.stringify({ channel: ctx.channel, sub }),
      });
      if (!response.ok) throw new Error('Subscription token refresh failed');

      const data = await response.json();
      if (data.token) return data.token;

      throw new Error('No subscription token in response');
    } catch (e) {
      throw e;
    }
  }

  function getSubFromToken() {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    const payload = JSON.parse(atob(token.split('.')[1]));

    return payload.sub;
  }

  async function fetchJwtToken() {
    try {
      const response = await fetch(`${apiUrl}1/jwt/token`);

      return await response.json();
    } catch (error) {
      throw new Error('Failed to fetch JWT token');
    }
  }

  async function refreshJwtToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        const formData = new FormData();
        formData.append('refresh_token', refreshToken);

        const response = await fetch(`${apiUrl}1/jwt/refresh`, {
          method: 'POST',
          body: formData,
        });

        if (response.status === 401) {
          // console.log('Refresh token expired, fetching new tokens...', dayjs().format());
          return await fetchJwtToken();
        } else {
          return await response.json();
        }
      } else {
        return await fetchJwtToken();
      }
    } catch (error) {
      throw new Error('Failed to refresh JWT token');
    }
  }

  function saveTokens(accessToken: string, refreshToken?: string) {
    localStorage.setItem('access_token', accessToken);

    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
  }

  return {
    initWebSockets,
  };
}
